export const getCoCounselError = (
  error,
  fallback = "Unable to update co-counsel. Please try again."
) => error?.response?.data?.error || error?.message || fallback;

export const getCoCounselAccountLabel = (firm) => {
  return (firm?.has_active_account ?? firm?.account_state === "active")
    ? "Account active"
    : "Account inactive";
};

export const getCoCounselInviteStatus = (relationship) => {
  if (relationship.status === "active") return "accepted";
  return (
    relationship.invite_status ||
    (relationship.invite?.rejected_at
      ? "rejected"
      : relationship.invite_sent_at || relationship.invite?.sent_at
        ? "sent"
        : "not_sent")
  );
};

export const formatCoCounselPhone = (value) => {
  const phone = String(value ?? "").trim();
  let digits = phone.replace(/\D/g, "");
  if (digits.length === 11 && digits.startsWith("1")) digits = digits.slice(1);
  if (digits.length !== 10) return phone;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const clean = (value) => String(value ?? "").trim();

export const buildCoCounselUserPayload = (values) => {
  const payload = values.attorney_directory_id
    ? { attorney_directory_id: Number(values.attorney_directory_id) }
    : {
        first_name: clean(values.first_name),
        last_name: clean(values.last_name),
        email: clean(values.email),
        phone: clean(values.phone).replace(/\D/g, ""),
      };
  if (!payload.attorney_directory_id) {
    if (!payload.first_name || !payload.last_name || !emailPattern.test(payload.email)) {
      throw new Error("Each team member needs a first name, last name, and valid email.");
    }
  }
  if (values.role_id) payload.role_id = Number(values.role_id);
  if (values.position) {
    const slot = Number(values.position);
    if (!Number.isInteger(slot) || slot < 1 || slot > 6) {
      throw new Error("Select a position from 1 to 6.");
    }
    payload.position = slot;
  }
  return payload;
};

export const buildCoCounselCreatePayload = ({ directory, firm, email, timezone }) => {
  if (directory) {
    if (!directory.id) throw new Error("Select a firm from the directory.");
    const payload = { attorneydirectory_id: directory.id, timezone, send_invite: false };
    if (!clean(directory.main_contact?.[0]?.email) && clean(email)) {
      if (!emailPattern.test(clean(email))) throw new Error("Enter a valid email for this firm.");
      payload.email = clean(email);
    }
    return payload;
  }
  const firmEmail = clean(email) || clean(firm?.email);
  if (!clean(firm?.firmName)) throw new Error("Enter the firm name.");
  if (firmEmail && !emailPattern.test(firmEmail)) throw new Error("Enter a valid firm email.");
  const team = (firm.teamData || [])
    .filter((member) => Object.values(member).some((value) => clean(value)))
    // Creation seats users in entry order, repeats the last user through slot six,
    // and applies each slot's default role on the server. Do not override roles or
    // send duplicate users through the positions PATCH endpoint.
    .map(({ first_name, last_name, email, phone }) =>
      buildCoCounselUserPayload({ first_name, last_name, email, phone })
    );
  if (team.length > 6) {
    throw new Error("Enter up to six team members.");
  }
  return {
    firm_name: clean(firm.firmName),
    ...(firmEmail ? { email: firmEmail } : {}),
    ...Object.fromEntries(
      ["address1", "address2", "city", "state", "zip", "website"].map((field) => [
        field,
        clean(firm[field]),
      ])
    ),
    phone: clean(firm.phone).replace(/\D/g, ""),
    fax: clean(firm.fax).replace(/\D/g, ""),
    timezone,
    send_invite: false,
    team,
  };
};
